import logError from 'src/data/services/logError';
import React, { useState, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import showAlert from 'src/core/utils/showAlert';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import NavBar from 'src/presentation/components/organisms/NavBar';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Footer from 'src/presentation/components/organisms/Footer';
import Loader from 'src/presentation/components/atoms/Loader';
import MyTimeTable from 'src/presentation/components/organisms/MyTimeTable';
import Title from 'src/presentation/components/atoms/Title';
import TimetableBlocks from 'src/presentation/components/organisms/TimetableBlocks';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList, TimeTablePageItem } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';
import isFavoritesPage from 'src/core/utils/isFavoritesPage';
import type { ApiPageTimetableDay, ApiPageTimetableHolder } from 'src/core/models/api/ApiPage';

function TimetableScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenTimetableDay>>();
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();
	const analyticsStore = useAnalyticsStore();

	const { page, isLoading: isPageLoading } = usePageFromRoute(route?.params);
	const routePageId = route?.params?.pageId;

	const [isLoading, setIsLoading] = useState(true);
	const [title, setTitle] = useState('');
	const [timetable, setTimetable] = useState<ApiPageTimetableDay | null>(null);

	// A day that cannot be resolved at all: nothing was handed over and the URL carries no page id
	// to look one up with. Only then is this the dead end the alert is for -- on a cold web load the
	// page is simply not there yet, and sending the visitor home would cut that short.
	const isDeadEnd = page === null && !isPageLoading && routePageId === undefined;

	useEffect(() => {
		if (!isDeadEnd) {
			return;
		}
		showAlert({
			title: Translations.errorWentWrongTitle,
			message: Translations.errorReturnToHome,
			confirmText: Translations.buttonOke,
			confirmButtonStyle: 'default',
			onConfirm: () => navigation.navigate(Constants.screenIndex),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isDeadEnd]);

	useEffect(() => {
		if (isPageLoading) {
			return;
		}
		if (!page) {
			// Nothing left to wait for, so stop the loader and let the empty state speak.
			setIsLoading(false);
			return;
		}

		const init = async () => {
			const t = page.name || '';
			const pID = page.id || 0;

			await eventStore.fetchEvent(navigationStore.activeEventUuid ?? '');
			if (isFavoritesPage(page)) {
				analyticsStore.timeTable(navigationStore.activeEventUuid ?? '');
			}

			// Read the fresh store state after the await; the mount-time snapshot is stale by now.
			const activeEventView = useEventStore.getState().activeEventView;
			const timeTables = activeEventView.find((p): p is ApiPageTimetableHolder => p.type === 'TimetableHolder');
			const tt = timeTables?.children.find((c) => c.id === pID);

			setTitle(t);
			setTimetable(tt ?? null);
			setIsLoading(false);
		};

		init().catch(logError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page?.id, isPageLoading]);

	const handleItemPress = (item: TimeTablePageItem, pressedTitle: string) => {
		navigation.navigate(Constants.screenTimeTablePage, { data: item, title: pressedTitle });
	};

	return (
		<View style={styles.root}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>
			{isLoading || isPageLoading ? (
				<Loader />
			) : (
				<View style={styles.root}>
					{isFavoritesPage(page) ? (
						<MyTimeTable onNavigateToTimetablePage={(params) => navigation.navigate(Constants.screenTimeTablePage, params)} />
					) : (
						<View style={styles.timetableContainer}>
							{timetable?.data.timetable && timetable.data.timetable.length > 0 ? (
								<>
									<Title text={title} />
									<TimetableBlocks
										dayTitle={title}
										endtime={timetable.data.endtime ?? 0}
										eventUuid={timetable.event_uuid}
										starttime={timetable.data.starttime ?? 0}
										timetable={timetable.data.timetable}
										onItemPress={handleItemPress}
									/>
								</>
							) : (
								<EmptyState
									icon="time-outline"
									message={Translations.textNoTimetableMessage}
									title={Translations.textNoTimetableTitle}
								/>
							)}
						</View>
					)}
				</View>
			)}
			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default TimetableScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	timetableContainer: {
		flex: 0.94,
	},
});
